import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query'
import { Link } from '@tanstack/react-router'
import { FileText } from 'lucide-react'
import { useState } from 'react'
import { toast } from 'sonner'
import { Markdown } from '../components/Markdown.tsx'
import { Badge, Button, Dialog, Field, Input, Select, Sheet } from '../components/ui.tsx'
import { get, post } from '../lib/api.ts'
import { OpinionBlock } from './opinions.tsx'
import { LOCALE, t } from '../lib/i18n.ts'

interface Report { id: string; period: string; documentId: string | null; visibility: 'todos' | 'conselho'; updatedAt: string }
/** "Setembro de 2026" / "September 2026": only the first letter is capitalised (CSS `capitalize` would also uppercase the "de"). */
const label = (p: string) => {
  const s = new Intl.DateTimeFormat(LOCALE, { month: 'long', year: 'numeric', timeZone: 'UTC' }).format(new Date(`${p.slice(0, 7)}-15T12:00:00Z`))
  return s.charAt(0).toUpperCase() + s.slice(1)
}
/** Last month in the condo's zone (the usual report), as `YYYY-MM`. */
const lastMonth = () => {
  const now = new Intl.DateTimeFormat('en-CA', { timeZone: 'America/Sao_Paulo', year: 'numeric', month: '2-digit' }).format(new Date())
  const y = Number(now.slice(0, 4))
  const m = Number(now.slice(5, 7))
  return m === 1 ? `${y - 1}-12` : `${y}-${String(m - 1).padStart(2, '0')}`
}

/** Relatório mensal (ADR 0022): one PDF per month in the acervo, from the app's own records; the síndico previews, generates and tells the residents. */
export function MonthlyReports({ condoId, slug, manage }: { condoId: string; slug: string; manage: boolean }) {
  const qc = useQueryClient()
  const q = useQuery({ queryKey: ['reports', condoId], queryFn: () => get<{ reports: Report[] }>(`/api/condos/${condoId}/reports/monthly`), enabled: Boolean(condoId) })
  const [period, setPeriod] = useState(lastMonth())
  const [visibility, setVisibility] = useState<'todos' | 'conselho'>('todos')
  const [notify, setNotify] = useState(true)
  const [narrative, setNarrative] = useState(false)
  const [preview, setPreview] = useState<string | null>(null)
  const prev = useMutation({ mutationFn: () => get<{ markdown: string }>(`/api/condos/${condoId}/reports/monthly/${period}/preview?visibility=${visibility}`), onSuccess: (r) => setPreview(r.markdown), onError: (e) => toast.error((e as Error).message) })
  const gen = useMutation({
    mutationFn: () => post<{ documentId: string; notified: number; narrative: boolean }>(`/api/condos/${condoId}/reports/monthly`, { period, visibility, notify, narrative }),
    onSuccess: (r) => { if (narrative && !r.narrative) toast.message(t('O assistente não estava disponível; o relatório saiu sem o parágrafo.')); toast.success(r.notified ? r.notified === 1 ? t('Relatório gerado e 1 pessoa avisada no WhatsApp') : t('Relatório gerado e {n} pessoas avisadas no WhatsApp', { n: r.notified }) : t('Relatório gerado no acervo')); setPreview(null); qc.invalidateQueries({ queryKey: ['reports', condoId] }) },
    onError: (e) => toast.error((e as Error).message),
  })
  const reports = q.data?.reports ?? []
  return (
    <section className="mb-5 grid gap-3">
      <div className="flex flex-wrap items-end gap-3">
        <h2 className="font-bold">{t('Relatório mensal')}</h2>
        <p className="basis-full text-sm text-tinta-2">{t('Um PDF por mês com o que aconteceu no condomínio, a partir dos registros do aplicativo: contas, chamados, reservas, avisos, assembleias, prazos e portaria. Vai para o acervo; os moradores podem ser avisados no WhatsApp.')}</p>
      </div>
      {manage && (
        <form className="grid gap-3 sm:grid-cols-[auto_auto_1fr] sm:items-end" onSubmit={(e) => { e.preventDefault(); gen.mutate() }}>
          <Field label={t('Mês')} htmlFor="rp-m"><Input id="rp-m" type="month" required value={period} onChange={(e) => setPeriod(e.target.value)} /></Field>
          <Field label={t('Quem vê')} htmlFor="rp-v">
            <Select id="rp-v" value={visibility} onChange={(e) => setVisibility(e.target.value as 'todos' | 'conselho')}><option value="todos">{t('Todos os moradores')}</option><option value="conselho">{t('Só o conselho e o síndico')}</option></Select>
          </Field>
          <div className="flex flex-wrap items-center gap-2">
            <label className="flex items-center gap-2 text-[0.95rem]"><input id="rp-n" type="checkbox" className="size-4" checked={notify} onChange={(e) => setNotify(e.target.checked)} />{' '}{t('Avisar no WhatsApp')}</label>
            <label className="flex items-center gap-2 text-[0.95rem]" title={t('Um parágrafo em palavras simples, escrito pelo assistente a partir dos números; os números continuam sendo a fonte.')}><input id="rp-ai" type="checkbox" className="size-4" checked={narrative} onChange={(e) => setNarrative(e.target.checked)} />{' '}{t('Parágrafo do assistente')}</label>
            <Button type="button" variant="outline" busy={prev.isPending} onClick={() => prev.mutate()}>{t('Pré-visualizar')}</Button>
            <Button type="submit" busy={gen.isPending}><FileText className="size-4" />{' '}{t('Gerar relatório')}</Button>
          </div>
        </form>
      )}
      {reports.length > 0 && (
        <Sheet>
          {reports.map((r) => (
            <div key={r.period} className="border-b border-pauta px-4 py-2 last:border-b-0">
              <div className="flex flex-wrap items-center gap-3">
                <span className="font-semibold">{label(r.period)}</span>
                <Badge tone={r.visibility === 'todos' ? 'carimbo' : 'neutral'}>{r.visibility === 'todos' ? 'todos' : 'conselho'}</Badge>
                {r.documentId && <Link to="/c/$slug/acervo/$docId" params={{ slug, docId: r.documentId }} className="ml-auto inline-flex items-center gap-1 text-sm font-semibold text-caneta"><FileText className="size-4" />{' '}{t('Ver no acervo')}</Link>}
              </div>
              <OpinionBlock condoId={condoId} kind="relatorio" subjectId={r.id} />
            </div>
          ))}
        </Sheet>
      )}
      <YearlyReports condoId={condoId} slug={slug} manage={manage} />
      <Dialog open={preview !== null} onClose={() => setPreview(null)} title={t('Prévia — {period}', { period: label(period) })} wide>
        {preview && (
          <div className="grid gap-3">
            <div className="overflow-x-auto rounded-ficha border border-pauta bg-folha p-4"><Markdown text={preview} /></div>
            <div className="flex justify-end gap-2"><Button variant="quiet" onClick={() => setPreview(null)}>{t('Fechar')}</Button><Button busy={gen.isPending} onClick={() => gen.mutate()}>{t('Gerar relatório')}</Button></div>
          </div>
        )}
      </Dialog>
    </section>
  )
}

// ======================= Prestação de contas anual (ADR 0032) =======================
interface YearlyReport { id: string; year: number; documentId: string | null; visibility: 'todos' | 'conselho'; updatedAt: string }

function YearlyReports({ condoId, slug, manage }: { condoId: string; slug: string; manage: boolean }) {
  const qc = useQueryClient()
  const q = useQuery({ queryKey: ['yearly-reports', condoId], queryFn: () => get<{ reports: YearlyReport[] }>(`/api/condos/${condoId}/reports/yearly`), enabled: Boolean(condoId) })
  const thisYear = new Date().getFullYear()
  const [year, setYear] = useState(thisYear)
  const [visibility, setVisibility] = useState<'todos' | 'conselho'>('conselho')
  const [narrative, setNarrative] = useState(false)
  const [preview, setPreview] = useState<string | null>(null)
  const prev = useMutation({ mutationFn: () => get<{ markdown: string }>(`/api/condos/${condoId}/reports/yearly/${year}/preview?visibility=${visibility}`), onSuccess: (r) => setPreview(r.markdown), onError: (e) => toast.error((e as Error).message) })
  const gen = useMutation({
    mutationFn: () => post<{ documentId: string; narrative: boolean }>(`/api/condos/${condoId}/reports/yearly`, { year, visibility, narrative }),
    onSuccess: (r) => { if (narrative && !r.narrative) toast.message(t('O assistente não estava disponível; o documento saiu sem o parágrafo.')); toast.success(t('Prestação de contas gerada no acervo')); setPreview(null); qc.invalidateQueries({ queryKey: ['yearly-reports', condoId] }) },
    onError: (e) => toast.error((e as Error).message),
  })
  const reports = q.data?.reports ?? []
  return (
    <div className="mt-2 grid gap-3">
      <div>
        <h3 className="font-bold">{t('Prestação de contas anual')}</h3>
        <p className="text-sm text-tinta-2">{t('Os relatórios mensais do ano em um PDF para a AGO: contas, balancetes e pareceres do conselho, previsão × realizado, chamados, reservas, avisos, assembleias, prazos e portaria.')}</p>
      </div>
      {manage && (
        <form className="grid gap-3 sm:grid-cols-[auto_auto_1fr] sm:items-end" onSubmit={(e) => { e.preventDefault(); gen.mutate() }}>
          <Field label={t('Ano')} htmlFor="yr-y"><Select id="yr-y" value={String(year)} onChange={(e) => setYear(Number(e.target.value))}>{[0, 1, 2].map((d) => <option key={d} value={thisYear - d}>{thisYear - d}</option>)}</Select></Field>
          <Field label={t('Quem vê')} htmlFor="yr-v">
            <Select id="yr-v" value={visibility} onChange={(e) => setVisibility(e.target.value as 'todos' | 'conselho')}><option value="conselho">{t('Só o conselho e o síndico')}</option><option value="todos">{t('Todos os moradores')}</option></Select>
          </Field>
          <div className="flex flex-wrap items-center gap-2">
            <label className="flex items-center gap-2 text-[0.95rem]"><input id="yr-ai" type="checkbox" className="size-4" checked={narrative} onChange={(e) => setNarrative(e.target.checked)} />{' '}{t('Parágrafo do assistente')}</label>
            <Button type="button" variant="outline" busy={prev.isPending} onClick={() => prev.mutate()}>{t('Prévia do ano')}</Button>
            <Button type="submit" busy={gen.isPending}><FileText className="size-4" />{' '}{t('Gerar PDF')}</Button>
          </div>
        </form>
      )}
      {reports.length > 0 && (
        <Sheet>
          {reports.map((r) => (
            <div key={r.id} className="flex flex-wrap items-center gap-3 border-b border-pauta px-4 py-2 last:border-b-0">
              <span className="font-semibold">{r.year}</span>
              <Badge tone={r.visibility === 'todos' ? 'carimbo' : 'neutral'}>{r.visibility === 'todos' ? 'todos' : 'conselho'}</Badge>
              {r.documentId && <Link to="/c/$slug/acervo/$docId" params={{ slug, docId: r.documentId }} className="ml-auto inline-flex items-center gap-1 text-sm font-semibold text-caneta"><FileText className="size-4" />{' '}{t('Ver no acervo')}</Link>}
            </div>
          ))}
        </Sheet>
      )}
      <Dialog open={preview !== null} onClose={() => setPreview(null)} title={t('Prestação de contas — {year}', { year })} wide>
        {preview && (
          <div className="grid gap-3">
            <div className="overflow-x-auto rounded-ficha border border-pauta bg-folha p-4"><Markdown text={preview} /></div>
            <div className="flex justify-end gap-2"><Button variant="quiet" onClick={() => setPreview(null)}>{t('Fechar')}</Button><Button busy={gen.isPending} onClick={() => gen.mutate()}>{t('Gerar PDF')}</Button></div>
          </div>
        )}
      </Dialog>
    </div>
  )
}
